import React, { useEffect, useState, useCallback } from 'react';
import { api } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { useAuth } from '@/lib/auth';
import {
  Mail, Send, CheckCircle2, XCircle, Clock, Eye, Trash2, Edit, Save,
  Play, ShieldAlert, RotateCw, Settings, Sparkles, Server, KeyRound, Loader2, Megaphone,
} from 'lucide-react';
import AnnouncementsTab from '@/components/marketplace/AnnouncementsTab';

const STATUSES = [
  { key: 'pending_review', label: 'Pending review', Icon: Clock, cls: 'bg-yellow-500/10 text-yellow-700 dark:text-yellow-500' },
  { key: 'approved', label: 'Approved', Icon: CheckCircle2, cls: 'bg-blue-500/10 text-blue-700 dark:text-blue-400' },
  { key: 'sent', label: 'Sent', Icon: CheckCircle2, cls: 'bg-[hsl(var(--verified))]/10 text-[hsl(var(--verified))]' },
  { key: 'failed', label: 'Failed', Icon: XCircle, cls: 'bg-[hsl(var(--danger))]/10 text-[hsl(var(--danger))]' },
  { key: 'discarded', label: 'Discarded', Icon: Trash2, cls: 'bg-muted text-muted-foreground' },
];

const StatusBadge = ({ status }) => {
  const s = STATUSES.find(x => x.key === status) || STATUSES[0];
  const { Icon } = s;
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${s.cls}`}
      data-testid={`email-status-${status}`}
    >
      <Icon className='h-3 w-3' /> {s.label}
    </span>
  );
};

const AdminEmails = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [tab, setTab] = useState('templates');

  return (
    <div className='space-y-4'>
      <div>
        <h1 className='text-2xl sm:text-3xl font-semibold tracking-tight flex items-center gap-2'>
          <Mail className='h-6 w-6 text-primary' /> Email engine
        </h1>
        <p className='text-muted-foreground text-sm'>
          Manage email templates, review the send queue, compose feature announcements, and control automatic vs manual review mode.
          {!isAdmin && <span className='ml-1 text-yellow-700 dark:text-yellow-500'>(Moderator: read-only for templates & settings; you can approve/discard queued emails.)</span>}
        </p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid='admin-emails-tabs'>
          <TabsTrigger value='templates' data-testid='admin-emails-tab-templates'>
            <Sparkles className='h-4 w-4 mr-1.5' /> Templates
          </TabsTrigger>
          <TabsTrigger value='queue' data-testid='admin-emails-tab-queue'>
            <Send className='h-4 w-4 mr-1.5' /> Queue
          </TabsTrigger>
          <TabsTrigger value='announcements' data-testid='admin-emails-tab-announcements'>
            <Megaphone className='h-4 w-4 mr-1.5' /> Announcements
          </TabsTrigger>
          <TabsTrigger value='settings' data-testid='admin-emails-tab-settings'>
            <Settings className='h-4 w-4 mr-1.5' /> Settings
          </TabsTrigger>
        </TabsList>

        <TabsContent value='templates' className='space-y-3 mt-4'>
          <TemplatesTab isAdmin={isAdmin} />
        </TabsContent>
        <TabsContent value='queue' className='space-y-3 mt-4'>
          <QueueTab />
        </TabsContent>
        <TabsContent value='announcements' className='space-y-3 mt-4'>
          <AnnouncementsTab isAdmin={isAdmin} />
        </TabsContent>
        <TabsContent value='settings' className='space-y-3 mt-4'>
          <SettingsTab isAdmin={isAdmin} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

// ============ Templates Tab ============
const TemplatesTab = ({ isAdmin }) => {
  const [templates, setTemplates] = useState([]);
  const [selectedTrigger, setSelectedTrigger] = useState(null);
  const [editing, setEditing] = useState(null);
  const [testEmail, setTestEmail] = useState('');
  const [testOpen, setTestOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/admin/emails/templates');
      setTemplates(data);
      if (!selectedTrigger && data.length) setSelectedTrigger(data[0].trigger);
    } catch (e) { toast.error('Failed to load templates'); }
  }, [selectedTrigger]);
  useEffect(() => { load(); }, [load]);

  const current = templates.find(t => t.trigger === selectedTrigger);

  const startEdit = () => setEditing({
    subject: current.subject,
    html_body: current.html_body,
    auto_send: current.auto_send,
    active: current.active,
    from_alias: current.from_alias || '',
    from_name: current.from_name || '',
    reply_to_alias: current.reply_to_alias || '',
  });
  const cancelEdit = () => setEditing(null);
  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/admin/emails/templates/${selectedTrigger}`, editing);
      toast.success('Template saved');
      setEditing(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to save'); }
    finally { setBusy(false); }
  };

  const doTestSend = async () => {
    if (!testEmail) return toast.error('Enter a recipient email');
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/emails/templates/${selectedTrigger}/test-send`, {
        recipient_email: testEmail,
      });
      toast.success(`Test email queued (status: ${data.status})`);
      setTestOpen(false);
      setTestEmail('');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  return (
    <div className='grid grid-cols-1 lg:grid-cols-4 gap-4'>
      {/* Left: template list */}
      <Card className='p-3 lg:col-span-1' data-testid='email-templates-list'>
        <div className='text-xs uppercase tracking-wider text-muted-foreground mb-2 px-1'>Triggers ({templates.length})</div>
        <div className='space-y-1'>
          {templates.map(t => (
            <button
              key={t.trigger}
              onClick={() => { setSelectedTrigger(t.trigger); setEditing(null); }}
              className={`w-full text-left rounded-md px-3 py-2 text-sm transition-colors ${selectedTrigger === t.trigger ? 'bg-primary text-primary-foreground' : 'hover:bg-secondary'}`}
              data-testid={`email-template-${t.trigger}`}
            >
              <div className='font-medium'>{t.name}</div>
              <div className={`text-[11px] mt-0.5 ${selectedTrigger === t.trigger ? 'text-primary-foreground/80' : 'text-muted-foreground'}`}>
                <code>{t.trigger}</code>
                {!t.active && ' · disabled'}
                {t.auto_send ? ' · auto' : ' · manual'}
              </div>
            </button>
          ))}
        </div>
      </Card>

      {/* Right: editor */}
      <Card className='p-5 lg:col-span-3' data-testid='email-template-editor'>
        {!current ? (
          <div className='text-sm text-muted-foreground'>Select a template on the left.</div>
        ) : (
          <div className='space-y-4'>
            <div className='flex items-start justify-between gap-3 flex-wrap'>
              <div>
                <div className='text-lg font-semibold'>{current.name}</div>
                <div className='text-xs text-muted-foreground mt-0.5'>{current.description}</div>
                <div className='text-[11px] text-muted-foreground mt-2 flex items-center gap-2 flex-wrap'>
                  <code className='rounded bg-secondary px-1.5 py-0.5'>trigger: {current.trigger}</code>
                  {current.auto_send ? (
                    <Badge variant='outline' className='text-[10px]'>auto-send</Badge>
                  ) : (
                    <Badge variant='outline' className='text-[10px]'>manual review</Badge>
                  )}
                  {!current.active && <Badge variant='destructive' className='text-[10px]'>disabled</Badge>}
                </div>
              </div>
              <div className='flex gap-2'>
                <Button size='sm' variant='outline' onClick={() => setTestOpen(true)} data-testid='email-test-send-btn'>
                  <Play className='h-3.5 w-3.5 mr-1' /> Send test
                </Button>
                {isAdmin && !editing && (
                  <Button size='sm' onClick={startEdit} data-testid='email-template-edit-btn'>
                    <Edit className='h-3.5 w-3.5 mr-1' /> Edit
                  </Button>
                )}
              </div>
            </div>

            {editing ? (
              <div className='space-y-3'>
                <div>
                  <Label>Subject</Label>
                  <Input
                    value={editing.subject}
                    onChange={(e) => setEditing({ ...editing, subject: e.target.value })}
                    data-testid='email-template-subject-input'
                  />
                </div>
                <div>
                  <Label>HTML body</Label>
                  <Textarea
                    rows={16}
                    value={editing.html_body}
                    onChange={(e) => setEditing({ ...editing, html_body: e.target.value })}
                    className='font-mono text-xs'
                    data-testid='email-template-body-input'
                  />
                  <div className='text-[11px] text-muted-foreground mt-1'>
                    Available variables: {(current.variables || []).map(v => <code key={v} className='rounded bg-secondary px-1 mx-0.5'>{`{{${v}}}`}</code>)}
                  </div>
                </div>
                <div className='grid md:grid-cols-3 gap-3 pt-2 border-t'>
                  <div className='space-y-1'>
                    <Label className='text-xs'>From alias (before @)</Label>
                    <Input value={editing.from_alias || ''} onChange={e => setEditing({ ...editing, from_alias: e.target.value })} placeholder='welcome / billing / support…' data-testid='email-template-from-alias' />
                    <p className='text-[10px] text-muted-foreground'>Empty = use SMTP default From</p>
                  </div>
                  <div className='space-y-1'>
                    <Label className='text-xs'>From display name</Label>
                    <Input value={editing.from_name || ''} onChange={e => setEditing({ ...editing, from_name: e.target.value })} placeholder='IndusFact Billing' data-testid='email-template-from-name' />
                  </div>
                  <div className='space-y-1'>
                    <Label className='text-xs'>Reply-To alias</Label>
                    <Input value={editing.reply_to_alias || ''} onChange={e => setEditing({ ...editing, reply_to_alias: e.target.value })} placeholder='support / billing / do-not-reply' data-testid='email-template-reply-to' />
                  </div>
                </div>
                <div className='flex items-center gap-6'>
                  <label className='flex items-center gap-2 text-sm'>
                    <Switch
                      checked={editing.auto_send}
                      onCheckedChange={(v) => setEditing({ ...editing, auto_send: v })}
                      data-testid='email-template-auto-toggle'
                    />
                    Auto-send (skip manual review)
                  </label>
                  <label className='flex items-center gap-2 text-sm'>
                    <Switch
                      checked={editing.active}
                      onCheckedChange={(v) => setEditing({ ...editing, active: v })}
                      data-testid='email-template-active-toggle'
                    />
                    Active
                  </label>
                </div>
                <div className='flex gap-2 justify-end'>
                  <Button variant='outline' onClick={cancelEdit}>Cancel</Button>
                  <Button onClick={save} disabled={busy} data-testid='email-template-save-btn'>
                    <Save className='h-3.5 w-3.5 mr-1' /> Save changes
                  </Button>
                </div>
              </div>
            ) : (
              <div className='space-y-3'>
                <div>
                  <div className='text-xs uppercase text-muted-foreground mb-1'>Subject</div>
                  <div className='font-medium text-sm border rounded-lg p-3 bg-secondary/40'>{current.subject}</div>
                </div>
                <div>
                  <div className='text-xs uppercase text-muted-foreground mb-1'>HTML preview</div>
                  <div
                    className='border rounded-lg p-4 bg-white text-sm text-black max-h-96 overflow-y-auto'
                    dangerouslySetInnerHTML={{ __html: current.html_body }}
                  />
                </div>
              </div>
            )}
          </div>
        )}
      </Card>

      {/* Test send dialog */}
      <Dialog open={testOpen} onOpenChange={setTestOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Send test email</DialogTitle></DialogHeader>
          <div className='space-y-3'>
            <div className='text-sm text-muted-foreground'>
              This will render the template with sample data and send it to the address below. Useful for previewing formatting in Gmail/Outlook.
            </div>
            <div>
              <Label>Recipient email</Label>
              <Input
                type='email'
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder='you@example.com'
                data-testid='email-test-input'
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant='outline' onClick={() => setTestOpen(false)}>Cancel</Button>
            <Button onClick={doTestSend} disabled={busy} data-testid='email-test-submit'>Send test</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============ Queue Tab ============
const QueueTab = () => {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [statusFilter, setStatusFilter] = useState('pending_review');
  const [triggerFilter, setTriggerFilter] = useState('');
  const [preview, setPreview] = useState(null); // queue item
  const [editing, setEditing] = useState(null); // {subject, html_body}
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (triggerFilter) params.trigger = triggerFilter;
      const { data } = await api.get('/admin/emails/queue', { params });
      setItems(data.items || []);
      setCounts(data.counts || {});
    } catch (e) { toast.error('Failed to load queue'); }
  }, [statusFilter, triggerFilter]);
  useEffect(() => { load(); }, [load]);

  const doSend = async (id) => {
    setBusy(true);
    try {
      await api.post(`/admin/emails/queue/${id}/send`);
      toast.success('Email sent');
      load();
      if (preview?.id === id) setPreview(null);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };
  const doDiscard = async (id) => {
    if (!window.confirm('Discard this pending email? It will not be sent.')) return;
    setBusy(true);
    try {
      await api.post(`/admin/emails/queue/${id}/discard`);
      toast.success('Discarded');
      load();
      if (preview?.id === id) setPreview(null);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };
  const doBulkApprove = async () => {
    if (!window.confirm(`Send all ${counts.pending_review || 0} pending emails?`)) return;
    setBusy(true);
    try {
      const { data } = await api.post('/admin/emails/queue/bulk-approve');
      toast.success(`Sent ${data.sent} / attempted ${data.attempted}`);
      load();
    } catch (e) { toast.error('Failed'); }
    finally { setBusy(false); }
  };
  const doEditSend = async () => {
    if (!editing) return;
    setBusy(true);
    try {
      await api.post(`/admin/emails/queue/${preview.id}/edit-and-send`, {
        subject: editing.subject,
        html_body: editing.html_body,
      });
      toast.success('Edited and sent');
      setEditing(null);
      setPreview(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  return (
    <div className='space-y-3'>
      {/* Counts strip */}
      <div className='grid grid-cols-2 md:grid-cols-5 gap-2' data-testid='email-queue-counts'>
        {STATUSES.map(s => (
          <button
            key={s.key}
            onClick={() => setStatusFilter(s.key)}
            className={`text-left rounded-lg border p-3 hover:bg-secondary/50 transition-colors ${statusFilter === s.key ? 'ring-2 ring-primary' : ''}`}
            data-testid={`email-queue-count-${s.key}`}
          >
            <div className='text-xs uppercase text-muted-foreground'>{s.label}</div>
            <div className='text-xl font-semibold mt-0.5 tabular-nums'>{counts[s.key] ?? 0}</div>
          </button>
        ))}
      </div>

      <Card className='p-3'>
        <div className='flex gap-2 items-center flex-wrap mb-3'>
          <Select value={statusFilter || '__all__'} onValueChange={(v) => setStatusFilter(v === '__all__' ? '' : v)}>
            <SelectTrigger className='w-52'><SelectValue placeholder='All statuses' /></SelectTrigger>
            <SelectContent>
              <SelectItem value='__all__'>All statuses</SelectItem>
              {STATUSES.map(s => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input
            placeholder='Filter by trigger key (e.g. welcome)'
            value={triggerFilter}
            onChange={(e) => setTriggerFilter(e.target.value)}
            className='w-64'
          />
          <Button size='sm' variant='outline' onClick={load} data-testid='email-queue-refresh'>
            <RotateCw className='h-3.5 w-3.5 mr-1' /> Refresh
          </Button>
          {statusFilter === 'pending_review' && (counts.pending_review || 0) > 0 && (
            <Button size='sm' onClick={doBulkApprove} disabled={busy} data-testid='email-queue-bulk-approve' className='ml-auto'>
              <Send className='h-3.5 w-3.5 mr-1' /> Approve + send all ({counts.pending_review})
            </Button>
          )}
        </div>

        {items.length === 0 ? (
          <div className='py-12 text-center text-sm text-muted-foreground'>No emails matching this filter.</div>
        ) : (
          <div className='overflow-x-auto'>
            <table className='w-full text-sm' data-testid='email-queue-table'>
              <thead className='text-left border-b'>
                <tr>
                  <th className='py-2 pr-3'>Trigger</th>
                  <th className='py-2 pr-3'>Recipient</th>
                  <th className='py-2 pr-3'>Subject</th>
                  <th className='py-2 pr-3'>Status</th>
                  <th className='py-2 pr-3'>Created</th>
                  <th className='py-2 pr-3'>Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map(it => (
                  <tr key={it.id} className='border-b last:border-b-0 hover:bg-secondary/40' data-testid={`email-row-${it.id}`}>
                    <td className='py-2 pr-3'>
                      <code className='text-xs'>{it.trigger}</code>
                    </td>
                    <td className='py-2 pr-3 text-muted-foreground'>
                      <div>{it.recipient_name || '—'}</div>
                      <div className='text-xs'>{it.recipient_email}</div>
                    </td>
                    <td className='py-2 pr-3 max-w-md truncate'>{it.subject}</td>
                    <td className='py-2 pr-3'><StatusBadge status={it.status} /></td>
                    <td className='py-2 pr-3 text-xs text-muted-foreground'>
                      {new Date(it.created_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                    </td>
                    <td className='py-2 pr-3'>
                      <div className='flex gap-1'>
                        <Button size='sm' variant='outline' onClick={() => { setPreview(it); setEditing(null); }} data-testid={`email-preview-${it.id}`}>
                          <Eye className='h-3.5 w-3.5' />
                        </Button>
                        {it.status === 'pending_review' && (
                          <>
                            <Button size='sm' variant='outline' onClick={() => doSend(it.id)} disabled={busy} data-testid={`email-send-${it.id}`}>
                              <Send className='h-3.5 w-3.5' />
                            </Button>
                            <Button size='sm' variant='ghost' onClick={() => doDiscard(it.id)} disabled={busy} data-testid={`email-discard-${it.id}`}>
                              <Trash2 className='h-3.5 w-3.5' />
                            </Button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Preview dialog */}
      <Dialog open={!!preview} onOpenChange={(v) => { if (!v) { setPreview(null); setEditing(null); } }}>
        <DialogContent className='max-w-3xl max-h-[85vh] overflow-y-auto' onInteractOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}>
          {preview && (
            <>
              <DialogHeader>
                <DialogTitle>
                  {preview.trigger} — <span className='text-muted-foreground text-sm'>{preview.recipient_email}</span>
                </DialogTitle>
              </DialogHeader>
              <div className='space-y-3'>
                <div className='flex items-center gap-2 flex-wrap text-xs'>
                  <StatusBadge status={preview.status} />
                  <span className='text-muted-foreground'>Created {new Date(preview.created_at).toLocaleString('en-IN')}</span>
                  {preview.sent_at && <span className='text-muted-foreground'>· Sent {new Date(preview.sent_at).toLocaleString('en-IN')}</span>}
                  {preview.error && <span className='text-[hsl(var(--danger))]'>· {preview.error}</span>}
                </div>
                {editing ? (
                  <div className='space-y-2'>
                    <div>
                      <Label>Subject</Label>
                      <Input value={editing.subject} onChange={(e) => setEditing({ ...editing, subject: e.target.value })} data-testid='email-edit-subject' />
                    </div>
                    <div>
                      <Label>HTML body</Label>
                      <Textarea rows={12} className='font-mono text-xs' value={editing.html_body} onChange={(e) => setEditing({ ...editing, html_body: e.target.value })} data-testid='email-edit-body' />
                    </div>
                  </div>
                ) : (
                  <>
                    <div>
                      <div className='text-xs uppercase text-muted-foreground mb-1'>Subject</div>
                      <div className='font-medium text-sm border rounded-lg p-3 bg-secondary/40'>{preview.subject}</div>
                    </div>
                    <div>
                      <div className='text-xs uppercase text-muted-foreground mb-1'>Body preview</div>
                      <div
                        className='border rounded-lg p-4 bg-white text-sm text-black max-h-72 overflow-y-auto'
                        dangerouslySetInnerHTML={{ __html: preview.html_body }}
                      />
                    </div>
                  </>
                )}
              </div>
              <DialogFooter className='gap-2 flex-wrap'>
                {preview.status === 'pending_review' && !editing && (
                  <Button variant='outline' onClick={() => setEditing({ subject: preview.subject, html_body: preview.html_body })} data-testid='email-preview-edit-btn'>
                    <Edit className='h-3.5 w-3.5 mr-1' /> Edit
                  </Button>
                )}
                {editing && (
                  <>
                    <Button variant='outline' onClick={() => setEditing(null)}>Cancel edit</Button>
                    <Button onClick={doEditSend} disabled={busy} data-testid='email-edit-send-btn'>
                      <Send className='h-3.5 w-3.5 mr-1' /> Save + send
                    </Button>
                  </>
                )}
                {preview.status === 'pending_review' && !editing && (
                  <>
                    <Button variant='ghost' onClick={() => doDiscard(preview.id)} disabled={busy} data-testid='email-preview-discard-btn'>
                      <Trash2 className='h-3.5 w-3.5 mr-1' /> Discard
                    </Button>
                    <Button onClick={() => doSend(preview.id)} disabled={busy} data-testid='email-preview-send-btn'>
                      <Send className='h-3.5 w-3.5 mr-1' /> Send now
                    </Button>
                  </>
                )}
                <Button variant='outline' onClick={() => { setPreview(null); setEditing(null); }}>Close</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============ Settings Tab ============
const SettingsTab = ({ isAdmin }) => {
  const [settings, setSettings] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sweepResult, setSweepResult] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/admin/emails/settings');
      setSettings(data);
    } catch (e) { toast.error('Failed'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggleManual = async (v) => {
    setBusy(true);
    try {
      const { data } = await api.patch('/admin/emails/settings', { manual_review_mode: v });
      setSettings(data);
      toast.success(v ? 'Manual review enabled for ALL emails' : 'Auto-send restored (per-template flag)');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  const runExpirySweep = async (dryRun) => {
    setBusy(true);
    setSweepResult(null);
    try {
      const { data } = await api.post(`/admin/emails/sweep/expiries?dry_run=${dryRun}`);
      setSweepResult(data);
      toast.success(dryRun ? `Dry-run: would enqueue ${data.total_enqueued}` : `Enqueued ${data.total_enqueued} expiry warnings`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  if (!settings) return <div className='text-sm text-muted-foreground py-6'>Loading…</div>;

  return (
    <div className='space-y-4'>
      <Card className='p-5' data-testid='email-settings-card'>
        <div className='flex items-start justify-between gap-4 flex-wrap'>
          <div>
            <div className='font-semibold flex items-center gap-2'>
              <ShieldAlert className='h-4 w-4' /> Manual review mode (hold every email in queue)
            </div>
            <div className='text-sm text-muted-foreground mt-1 max-w-2xl'>
              <strong>ON</strong> — every outgoing email lands in the queue for you to review and send manually. Nothing is sent automatically. Use this right after go-live to sanity-check every trigger.<br />
              <strong>OFF</strong> — respects each template&apos;s own auto-send flag (transactional emails go instantly; marketing ones can still be held per-template).
            </div>
          </div>
          <div className='flex flex-col items-end gap-1.5'>
            <Switch
              checked={settings.manual_review_mode}
              onCheckedChange={toggleManual}
              disabled={busy || !isAdmin}
              data-testid='email-settings-manual-toggle'
            />
            <Badge className={settings.manual_review_mode
              ? 'bg-amber-100 text-amber-800 border-amber-200'
              : 'bg-emerald-100 text-emerald-800 border-emerald-200'
            }>
              {settings.manual_review_mode ? 'Queue mode (manual)' : 'Auto-send'}
            </Badge>
          </div>
        </div>
        {settings.updated_at && (
          <div className='text-[11px] text-muted-foreground mt-3'>
            Last updated: {new Date(settings.updated_at).toLocaleString('en-IN')}
          </div>
        )}
      </Card>

      <SmtpConfigCard isAdmin={isAdmin} />

      <Card className='p-5' data-testid='email-settings-expiry'>
        <div className='font-semibold flex items-center gap-2'>
          <Clock className='h-4 w-4' /> Listing expiry sweep
        </div>
        <div className='text-sm text-muted-foreground mt-1 max-w-2xl'>
          Listings expire 60 days after posting. This sweep queues warning emails for listings expiring in <strong>2 days</strong> and <strong>1 day</strong>. Duplicates are prevented via a per-listing marker. Run this from a daily cron, or manually below.
        </div>
        <div className='flex gap-2 mt-3'>
          <Button size='sm' variant='outline' onClick={() => runExpirySweep(true)} disabled={busy} data-testid='email-sweep-dryrun'>
            Dry-run
          </Button>
          <Button size='sm' onClick={() => runExpirySweep(false)} disabled={busy} data-testid='email-sweep-run'>
            Run sweep now
          </Button>
        </div>
        {sweepResult && (
          <div className='mt-3 text-sm border rounded-lg p-3 bg-secondary/40'>
            <div>Enqueued <strong>{sweepResult.total_enqueued}</strong> emails</div>
            <div className='text-xs text-muted-foreground mt-1'>
              2-day warnings: {sweepResult.per_days_left?.['2'] ?? 0}, 1-day warnings: {sweepResult.per_days_left?.['1'] ?? 0}
              {sweepResult.dry_run ? ' (dry-run, no changes saved)' : ''}
            </div>
          </div>
        )}
      </Card>
    </div>
  );
};

export default AdminEmails;

// ============ SMTP Config Card ============
const SmtpConfigCard = ({ isAdmin }) => {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [form, setForm] = useState({});
  const [password, setPassword] = useState('');
  const [testEmail, setTestEmail] = useState('');
  const [showPw, setShowPw] = useState(false);
  const initRef = React.useRef(false);
  const inflightRef = React.useRef(false);
  const baselineRef = React.useRef({});

  const load = useCallback(async () => {
    if (inflightRef.current) return;
    inflightRef.current = true;
    setLoading(true);
    try {
      const { data } = await api.get('/admin/emails/smtp');
      setCfg(data);
      if (!initRef.current) {
        const seeded = {
          host: data.host || '',
          port: data.port || 587,
          username: data.username || '',
          use_tls: !!data.use_tls,
          use_ssl: !!data.use_ssl,
          default_from_email: data.default_from_email || '',
          default_from_name: data.default_from_name || '',
          default_reply_to: data.default_reply_to || '',
        };
        setForm(seeded);
        baselineRef.current = { ...seeded };
        setPassword('');
        initRef.current = true;
      }
    } catch (e) { toast.error('Failed to load SMTP config'); }
    finally { setLoading(false); inflightRef.current = false; }
  }, []);
  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      // Only send fields the admin actually changed
      const payload = {};
      const base = baselineRef.current || {};
      Object.entries(form).forEach(([k, v]) => {
        if (v !== base[k]) payload[k] = v;
      });
      if (password !== '') payload.password = password;
      if (Object.keys(payload).length === 0) {
        toast.info('No changes to save.');
        return;
      }
      const { data } = await api.patch('/admin/emails/smtp', payload);
      setCfg(data);
      // New baseline = the just-saved values
      baselineRef.current = {
        host: data.host || '',
        port: data.port || 587,
        username: data.username || '',
        use_tls: !!data.use_tls,
        use_ssl: !!data.use_ssl,
        default_from_email: data.default_from_email || '',
        default_from_name: data.default_from_name || '',
        default_reply_to: data.default_reply_to || '',
      };
      setPassword('');
      toast.success('SMTP config saved. Next email will use these settings.');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const test = async () => {
    if (!testEmail) {
      toast.error('Enter a recipient email to test');
      return;
    }
    setTesting(true);
    try {
      const payload = { recipient_email: testEmail };
      // If user has typed inline changes (host/port/user/password), test THOSE
      if (password) payload.password = password;
      const base = baselineRef.current || {};
      Object.entries(form).forEach(([k, v]) => {
        if (v !== '' && v !== undefined && v !== null && v !== base[k]) payload[k] = v;
      });
      const { data } = await api.post('/admin/emails/smtp/test', payload);
      toast.success(data.message || 'Test email sent — check inbox');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Test failed'); }
    finally { setTesting(false); }
  };

  if (loading || !cfg) {
    return <Card className='p-5 text-sm text-muted-foreground' data-testid='smtp-loading'>Loading SMTP config…</Card>;
  }

  const srcBadge = (s) => {
    if (s === 'db') return <Badge className='bg-emerald-100 text-emerald-800 border-emerald-200 text-[10px]'>DB</Badge>;
    if (s === 'env') return <Badge className='bg-blue-100 text-blue-800 border-blue-200 text-[10px]'>.env</Badge>;
    return <Badge variant='outline' className='text-muted-foreground text-[10px]'>—</Badge>;
  };

  return (
    <Card className='p-5 space-y-5' data-testid='smtp-config-card'>
      <div className='flex items-start justify-between gap-4 flex-wrap'>
        <div>
          <div className='font-semibold flex items-center gap-2'>
            <Server className='h-4 w-4' /> SMTP server (Hostinger / any provider)
          </div>
          <p className='text-sm text-muted-foreground mt-1 max-w-2xl'>
            Paste your Hostinger email credentials. Emails will start flowing through your own domain. Hot-reloads instantly — no server restart.
          </p>
        </div>
        {cfg.enabled ? (
          <Badge className='bg-emerald-600 text-white'>SMTP LIVE</Badge>
        ) : (
          <Badge variant='outline' className='text-amber-700 border-amber-300 bg-amber-50'>Not configured</Badge>
        )}
      </div>

      <div className='grid md:grid-cols-2 gap-4'>
        <div className='space-y-1.5'>
          <div className='flex items-center justify-between'>
            <Label htmlFor='smtp-host'>SMTP Host</Label>
            {srcBadge(cfg.source?.host)}
          </div>
          <Input id='smtp-host' value={form.host || ''} onChange={e => set('host', e.target.value)} placeholder='smtp.hostinger.com' data-testid='smtp-host' />
        </div>
        <div className='space-y-1.5'>
          <div className='flex items-center justify-between'>
            <Label htmlFor='smtp-port'>Port</Label>
            {srcBadge(cfg.source?.port)}
          </div>
          <Input id='smtp-port' type='number' value={form.port || 587} onChange={e => set('port', parseInt(e.target.value || '587', 10))} data-testid='smtp-port' />
          <p className='text-xs text-muted-foreground'>Hostinger: <strong>465</strong> (SSL) or <strong>587</strong> (STARTTLS)</p>
        </div>
        <div className='space-y-1.5'>
          <div className='flex items-center justify-between'>
            <Label htmlFor='smtp-user'>Username (real mailbox)</Label>
            {srcBadge(cfg.source?.username)}
          </div>
          <Input id='smtp-user' value={form.username || ''} onChange={e => set('username', e.target.value)} placeholder='mailer@indusfact.com' data-testid='smtp-username' />
        </div>
        <div className='space-y-1.5'>
          <div className='flex items-center justify-between'>
            <Label htmlFor='smtp-pw'>Password</Label>
            <div className='flex items-center gap-2'>
              {cfg.password_masked && <span className='text-xs text-muted-foreground font-mono'>{cfg.password_masked}</span>}
              {srcBadge(cfg.source?.password)}
            </div>
          </div>
          <div className='flex items-center gap-2'>
            <Input id='smtp-pw' type={showPw ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} placeholder={cfg.password_masked ? 'Leave blank to keep' : 'Hostinger mailbox password'} data-testid='smtp-password' autoComplete='new-password' />
            <Button type='button' variant='outline' size='sm' onClick={() => setShowPw(v => !v)} data-testid='smtp-password-toggle'>{showPw ? 'Hide' : 'Show'}</Button>
          </div>
        </div>
      </div>

      <div className='grid md:grid-cols-2 gap-4'>
        <div className='flex items-center gap-2'>
          <Switch id='smtp-tls' checked={!!form.use_tls} onCheckedChange={v => set('use_tls', v)} data-testid='smtp-use-tls' />
          <Label htmlFor='smtp-tls'>STARTTLS (port 587)</Label>
        </div>
        <div className='flex items-center gap-2'>
          <Switch id='smtp-ssl' checked={!!form.use_ssl} onCheckedChange={v => set('use_ssl', v)} data-testid='smtp-use-ssl' />
          <Label htmlFor='smtp-ssl'>Implicit SSL (port 465)</Label>
        </div>
      </div>

      <div className='pt-3 border-t space-y-3'>
        <div className='text-sm font-medium'>Default sender identity</div>
        <div className='grid md:grid-cols-3 gap-4'>
          <div className='space-y-1.5'>
            <Label htmlFor='smtp-from-name'>From name</Label>
            <Input id='smtp-from-name' value={form.default_from_name || ''} onChange={e => set('default_from_name', e.target.value)} placeholder='IndusFact B2B Solutions' data-testid='smtp-from-name' />
          </div>
          <div className='space-y-1.5'>
            <Label htmlFor='smtp-from-email'>From email (fallback if template has no alias)</Label>
            <Input id='smtp-from-email' value={form.default_from_email || ''} onChange={e => set('default_from_email', e.target.value)} placeholder='mailer@indusfact.com' data-testid='smtp-from-email' />
          </div>
          <div className='space-y-1.5'>
            <Label htmlFor='smtp-reply-to'>Default Reply-To</Label>
            <Input id='smtp-reply-to' value={form.default_reply_to || ''} onChange={e => set('default_reply_to', e.target.value)} placeholder='support@indusfact.com' data-testid='smtp-reply-to' />
          </div>
        </div>
        <p className='text-xs text-muted-foreground'>
          Per-trigger aliases (welcome@, billing@, moderation@, security@, notifications@, support@) are set on each email template. Empty alias = uses this default From.
        </p>
      </div>

      <div className='pt-3 border-t space-y-2'>
        <div className='text-sm font-medium'>Test connection</div>
        <div className='flex flex-wrap items-center gap-2'>
          <Input value={testEmail} onChange={e => setTestEmail(e.target.value)} placeholder='Send a test email to…' className='max-w-sm' data-testid='smtp-test-email' />
          <Button variant='outline' onClick={test} disabled={testing || !isAdmin} data-testid='smtp-test-btn'>
            {testing ? <Loader2 className='h-4 w-4 mr-1 animate-spin' /> : <KeyRound className='h-4 w-4 mr-1' />}
            Send test
          </Button>
        </div>
        <p className='text-xs text-muted-foreground'>Uses either your unsaved-but-typed values (if password entered) or the saved credentials.</p>
      </div>

      <div className='pt-3 border-t flex items-center gap-2'>
        <Button onClick={save} disabled={saving || !isAdmin} data-testid='smtp-save-btn'>
          {saving ? <Loader2 className='h-4 w-4 mr-1 animate-spin' /> : <Save className='h-4 w-4 mr-1' />}
          Save SMTP config
        </Button>
        {cfg.updated_at && (
          <span className='text-xs text-muted-foreground ml-auto'>
            Updated by {cfg.updated_by_email || '—'} · {new Date(cfg.updated_at).toLocaleString('en-IN')}
          </span>
        )}
      </div>
    </Card>
  );
};
